import React, { useState, useMemo } from "react";
import { CancelPlanModal } from "./CancelPlanModal";
import { CancelPlanOfferRouter } from "./CancelPlanOfferRouter";
import subscriptionsApi from "../../../../../backendapi/subscriptionsApi";
import moment from "moment";
import {
    frequencies,
    getPlanPricing
} from "../../../../../clientServices/workspaceFeaturesCredits";
import { DowngradePlanTargets, WorkspaceUsage } from "../../../../../state/types";
import { DowngradePlanSurveyDialog } from "../../StarterTrial/DowngradePlanSurveyDialog";
import { RfModal } from "../../RfModal/RfModal";
import { isFreeOrTrialPlan, isFreePlan } from "@functions/workspacePlans";
import { getPricePerCredit } from "../../../../utils/ubpUtils";
import { isCreditSystemEnabled } from "../../../../routes/workspace/utils";
import {
    downgradeToPublicPlan,
    downgradeToSandboxPlan,
    fetchCheckoutUrlAndRedirect
} from "../../../../../backendapi/workspaceApi";
import { useStore } from "zustand";
import { useGlobalStore } from "../../../../../state/StoreContext";
import { usePaidPlanSubscription } from "../../../hooks/usePaidPlanSubscription";
import { useProjectSelector } from "../../../hooks/useProjectSelector";
import {
    getEligibleCancellationOffer,
    CancellationOfferDefinition
} from "@functions/plans/info/cancellationOffers";

type CancelPlanControllerProps = {
    usage: WorkspaceUsage;
    isOpen: boolean;
    onClose: () => void;
    onCloseParentModal?: () => void;
    subscription?: any;
    selectedDowngradePlan: DowngradePlanTargets;
    selectedFrequency?: { interval: string; labelWithoutDiscount: string } | null;
    fsrc: string;
    expiredTrial?: boolean;
};

type CancelPlanSteps = "usage" | "offer" | "postCancellationSurvey";

export function CancelPlanController({
    usage,
    isOpen,
    onClose,
    onCloseParentModal,
    selectedDowngradePlan,
    selectedFrequency,
    fsrc,
    expiredTrial = false
}: CancelPlanControllerProps) {
    const [step, setStep] = useState<CancelPlanSteps>("usage");
    const [frequency, setFrequency] = useState(selectedFrequency || frequencies[1]);
    const [activeOffer, setActiveOffer] = useState<CancellationOfferDefinition | null>(null);

    const projectSelector = useProjectSelector();
    const planPricing = useMemo(() => getPlanPricing(projectSelector), [projectSelector]);

    const { subscription, fetchPaidPlanSubscription } = usePaidPlanSubscription();

    const globalStore = useGlobalStore();
    const { uid } = useStore(globalStore, (state) => ({ uid: state.user.uid }));

    const isCreditBased = isCreditSystemEnabled(usage);

    const freeOrTrial = isFreeOrTrialPlan(usage);
    const free = isFreePlan(usage);

    const handleClose = () => {
        setStep("usage");
        setActiveOffer(null);
        onClose();
    };

    const showErrorMessage = (type: "cancel" | "downgrade", errorResponse) => {
        if (errorResponse?.error === "No active subscription found to cancel.") {
            swal.fire({
                title: "Subscription cancelled",
                text: "Your subscription cancellation has been scheduled. Your plan will not renew at the end of the current period.",
                customClass: { container: "newDialog" }
            });
        } else {
            swal.fire({
                type: "error",
                title: type === "cancel" ? "Error canceling subscription" : "Error applying offer",
                text:
                    (type === "cancel"
                        ? `There was an error canceling your subscription. `
                        : `There was an error downgrading to the Core Plan. `) +
                    "Please try again or contact support@roboflow.com if this issue persists.",
                customClass: { container: "newDialog destructive" }
            });
        }
        handleClose();
    };

    const handleCancelPlan = async ({ basicDowngradeFrequency = null } = {}) => {
        try {
            const isDowngradeToBasicOrCore =
                selectedDowngradePlan === "basic" ||
                selectedDowngradePlan === "core" ||
                basicDowngradeFrequency;

            if (isDowngradeToBasicOrCore) {
                const targetPlan = basicDowngradeFrequency ? "core" : selectedDowngradePlan;
                const downgradeFrequency = basicDowngradeFrequency || frequency;
                setFrequency(downgradeFrequency);

                const downgradePlanType =
                    downgradeFrequency.interval === "annually" ? `${targetPlan}Annual` : targetPlan;

                if (freeOrTrial) {
                    const stripePlanKey = targetPlan === "core" ? "core_plan" : "basic_plan";
                    const priceId =
                        downgradeFrequency.interval === "annually"
                            ? projectSelector.stripe()[stripePlanKey].annualPriceId
                            : projectSelector.stripe()[stripePlanKey].priceId;
                    await fetchCheckoutUrlAndRedirect({
                        workspace_id: usage?.id,
                        uid,
                        plan: targetPlan,
                        funnel_source: fsrc,
                        priceId,
                        price: planPricing[targetPlan][downgradeFrequency.interval].totalPrice,
                        interval: downgradeFrequency.interval
                    });
                    return;
                } else {
                    const response = await subscriptionsApi.schedulePlanDowngrade(
                        downgradePlanType,
                        usage?.id
                    );
                    if (!response?.success) {
                        throw new Error("Failed to schedule plan downgrade");
                    }
                }
            } else if (!freeOrTrial) {
                const response = await subscriptionsApi.cancelPaidPlanSubscription(usage?.id);
                if (!response?.success) {
                    throw new Error("Failed to cancel subscription");
                }
            } else if (selectedDowngradePlan === "public") {
                await downgradeToPublicPlan(usage?.id);
            } else {
                // should only happen when the user manually downgrades before their trial ends
                await downgradeToSandboxPlan(usage?.id);
            }

            setStep("postCancellationSurvey");
        } catch (e) {
            const downgrade = e.message?.includes("downgrade") ? "downgrade" : "cancel";
            showErrorMessage(downgrade, e.responseJSON);
        }
    };

    const handleUsageNext = async () => {
        const offer = getEligibleCancellationOffer({
            plan: usage?.plan,
            isFreeOrTrial: freeOrTrial,
            isCreditBased,
            hasAcceptedCancelOffer: !!usage?.acceptedCancelOffer,
            selectedDowngradePlan,
            expiredTrial
        });

        if (offer) {
            setActiveOffer(offer);
            setStep("offer");
            eventTracker.event({ type: "Cancellation Offer Shown", data: { offer_id: offer.id } });
        } else {
            await handleCancelPlan();
        }
    };

    switch (step) {
        case "usage":
            return (
                <CancelPlanModal
                    usage={usage}
                    isOpen={isOpen}
                    onClose={handleClose}
                    onNext={handleUsageNext}
                    isDowngrade={!free}
                    expiredTrial={expiredTrial}
                    selectedDowngradePlan={selectedDowngradePlan}
                />
            );
        case "offer":
            return (
                <CancelPlanOfferRouter
                    isOpen={isOpen}
                    onClose={handleClose}
                    onCancel={handleCancelPlan}
                    offer={activeOffer!}
                    onAcceptCreditsOffer={async (offerId) => {
                        const result = await subscriptionsApi.acceptCancelOffer(
                            offerId,
                            fsrc,
                            usage?.id
                        );
                        handleClose();
                        return result;
                    }}
                    onAcceptDowngrade={(basicDowngradeFrequency) =>
                        handleCancelPlan({ basicDowngradeFrequency })
                    }
                    pricePerCredit={getPricePerCredit({ usage })}
                    currentPlan={usage?.plan}
                />
            );
        case "postCancellationSurvey":
            return (
                <RfModal isOpen={isOpen} onClose={() => {}}>
                    <RfModal.Panel className="!w-fit !p-0">
                        <RfModal.CloseButton
                            onClick={async () => {
                                await fetchPaidPlanSubscription({ refetch: true });
                                onCloseParentModal?.();
                                handleClose();
                            }}
                        />
                        <DowngradePlanSurveyDialog
                            handleClose={() => {
                                onCloseParentModal?.();
                                handleClose();
                            }}
                            selectedPlan={selectedDowngradePlan}
                            onUndoClick={
                                freeOrTrial
                                    ? null
                                    : async () => {
                                          await subscriptionsApi.reactivatePaidPlanSubscription(
                                              usage?.id
                                          );
                                          await fetchPaidPlanSubscription({ refetch: true });
                                          handleClose();
                                      }
                            }
                            subscriptionEndDate={moment(subscription?.renewedAt * 1000).format(
                                "LL"
                            )}
                            currentPlan={usage?.plan}
                            frequency={frequency}
                            expiredTrial={expiredTrial}
                            usage={usage}
                        />
                    </RfModal.Panel>
                </RfModal>
            );
        default:
            throw new Error("Invalid cancellation step");
    }
}
